/**
 * SOLID Review
 * S — Strong. One concern: the server's ad-provider decision for a player, as the `ClientAdsServer` port
 *     speaks it.
 * O — Good. A new field the screen reads is one more line in `configOf`.
 * L — Strong. Implements the port interface exactly.
 * I — Strong. The port's one op.
 * D — Good. The Application-declared Apollo adapter interface; the Application's port.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPayloadAdConfig, IPayloadAdConfigRequest, IEntityAdConfig } from '../../domain/ports/schemas/AdsMoves';
import type { IPortClientAdsServer } from '../../application/ports/contracts';
import type { IAdapterApolloAds, IEntityAdProviderConfig } from '../../application/ads/ports/IAdapterApolloAds';
import { restCall, type TRestResult } from '../contract/restResult';

function configOf(answer: IEntityAdProviderConfig): IPayloadAdConfig {
  return { provider: answer.provider, adsensePublisherId: answer.adsensePublisherId ?? null, adsenseSlotId: answer.adsenseSlotId ?? null };
}

/**
 * THE AD-PROVIDER GATEWAY (frontend strict-ports plan, FE4d, 2026-10-04) — the Framework's `AdsAdapter`
 * socket, moved behind the port: the Apollo query is `ApolloAdsAdapter`'s, unchanged (network-only, so a
 * returning player is not pinned to a stale arm of the experiment). What the screen reads of the answer —
 * the provider and the AdSense slot — is all that crosses; a query that throws could not reach the server.
 */
export class ClientAdsServerAdapter implements IPortClientAdsServer {
  constructor(private readonly ads: IAdapterApolloAds) {}

  async adConfig(_ctx: IContextRequest, request: IPayloadAdConfigRequest): Promise<TRestResult<IEntityAdConfig>> {
    return restCall(async (): Promise<IEntityAdConfig> => {
      const answer = await this.ads.fetchAdProviderConfig(request.playerId);
      return { config: answer === null ? null : configOf(answer) };
    });
  }
}
